import { Link } from "@tanstack/react-router";
import { useState } from "react";
import { Button, buttonClass } from "../../components/ui/Button";
import { EmptyState, Spinner } from "../../components/ui/misc";
import { Panel } from "../../components/ui/Panel";
import type { Site } from "../../lib/api/models";
import { can, useMe } from "../../lib/auth";
import { useSites } from "../../lib/queries";
import { useClient } from "./client-ui";
import { SiteNotesDialog } from "./SiteNotesDialog";

/**
 * The Sites tab: a panel with a table as the other tabs are. The rows are Gorelo's locations of this client;
 * name and address come from Gorelo, the notes are Docs' own.
 */
export function ClientSites({ clientId }: { clientId: string }) {
  const me = useMe();
  const { client } = useClient(clientId);
  const { data: sites, isLoading } = useSites(clientId);
  const [editing, setEditing] = useState<Site | null>(null);
  // A client from before the sync is read-only.
  const canEdit = can(me, "edit") && client?.synced === true;
  const linked = client?.synced === true;

  return (
    <Panel aria-label="Sites" title="Sites">
      {isLoading ? <Spinner label="Loading sites…" /> : null}
      {sites && sites.length === 0 ? (
        <EmptyState
          title="No sites yet"
          body="A site is one of the client's locations in Gorelo. Add it there; it appears here after the next sync."
        />
      ) : null}
      {sites && sites.length > 0 ? (
        <div className="overflow-x-auto">
          <table aria-label="Sites" className="kd-datatable min-w-[760px]">
            <thead>
              <tr>
                <th>Site</th>
                <th>Address</th>
                <th>Devices</th>
                <th>Network items</th>
                <th>Notes</th>
                {linked ? (
                  <th>
                    <span className="sr-only">Actions</span>
                  </th>
                ) : null}
              </tr>
            </thead>
            <tbody>
              {sites.map((s) => (
                <tr key={s.id}>
                  <td className="font-semibold whitespace-nowrap text-ink">{s.name}</td>
                  <td>{s.address}</td>
                  <td className="tabular-nums">{s.counts.device}</td>
                  <td className="tabular-nums">{s.counts.network}</td>
                  <td className="whitespace-pre-line">{s.notes}</td>
                  {linked ? (
                    <td className="text-right whitespace-nowrap">
                      <Link
                        to="/clients/$clientId/diagram/$siteId"
                        params={{ clientId, siteId: s.id }}
                        aria-label={`Diagram of ${s.name}`}
                        className={buttonClass({ variant: "ghost", size: "sm" })}
                      >
                        Diagram
                      </Link>
                      {canEdit ? (
                        <Button
                          size="sm"
                          variant="ghost"
                          aria-label={`Edit the notes of ${s.name}`}
                          onClick={() => setEditing(s)}
                        >
                          Edit notes
                        </Button>
                      ) : null}
                    </td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
      <SiteNotesDialog clientId={clientId} site={editing} onClose={() => setEditing(null)} />
    </Panel>
  );
}
